import { watch, computed, unref } from 'vue'
import { useQuery, useInfiniteQuery, useMutation } from '@tanstack/vue-query'
import { $axios, $queryClient } from '@/boot/initApi'

const QUERY_KEY = ['watchlists']

const invalidate = () => $queryClient.invalidateQueries({ queryKey: QUERY_KEY })
// Writes return the fresh watchlist, whose `updatedAt` is the token the next save sends as
// `expectedUpdatedAt`; putting it straight into the cache keeps a quick second save from
// carrying the previous value (a false 409) while the invalidation refetch is still in flight.
const cacheWatchlist = (id, watchlist) => {
  if (watchlist?.id) $queryClient.setQueryData([...QUERY_KEY, String(id)], watchlist)
}
// Deliberately NOT under ['watchlists']: every write invalidates that root, and a changes list
// that refetched on each one would re-sort under the user's pointer (marking a change seen moves
// it out of the "unread" group). New arrivals surface through an explicit "Refresh" instead, and
// seen state is patched into this cache in place.
const CHANGES_KEY = ['watchlist-changes']
// Also not under ['watchlists'] for the same reason as CHANGES_KEY — a criteria save already
// re-seeds the page from its own response, and the results list doesn't need to react to every
// unrelated watchlist mutation (renames, other watchlists' writes).
const RESULTS_KEY = ['watchlist-results']

const patchChanges = (watchlistId, patch) => $queryClient.setQueriesData(
  { queryKey: [...CHANGES_KEY, String(watchlistId)] },
  cached => cached && { ...cached, pages: cached.pages.map(page => ({ ...page, data: page.data.map(patch) })) }
)

// GET /changes query string: unset/empty options are simply left out.
const changesQueryParams = view => ({
  ...(view?.sort ? { sort: view.sort } : {}),
  ...(view?.reason?.length ? { reason: view.reason } : {}),
  ...(view?.entityType?.length ? { entity_type: view.entityType } : {}),
  ...(view?.unseen ? { unseen: 1 } : {})
})

export default ({ watchlists, currentWatchlist, unreadTotal }) => ({
  indexWatchlists: ({ enabled = true } = {}) => {
    const query = useQuery({
      queryKey: QUERY_KEY,
      queryFn: async () => {
        const { data } = await $axios.get('/api/watchlists')
        return data ?? []
      },
      enabled,
      staleTime: 1000 * 60 * 2
    }, $queryClient)

    watch(query.data, val => {
      if (val) {
        watchlists.value = val
        unreadTotal.value = val.reduce((sum, w) => sum + (w.unreadChangesCount ?? 0), 0)
      }
    }, { immediate: true })

    return query
  },

  showWatchlist: ({ id, enabled = true } = {}) => {
    const query = useQuery({
      queryKey: computed(() => [...QUERY_KEY, unref(id)]),
      queryFn: async () => {
        const { data } = await $axios.get(`/api/watchlists/${unref(id)}`)
        return data?.id ? data : null
      },
      enabled,
      staleTime: 1000 * 60 * 2,
      // A missing or deleted watchlist is a definite answer, not a hiccup worth retrying.
      retry: (failureCount, error) => error?.response?.status !== 404 && failureCount < 2
    }, $queryClient)

    watch(query.data, val => {
      if (val) currentWatchlist.value = val
    }, { immediate: true })

    return query
  },

  storeWatchlist: () => useMutation({
    mutationFn: payload => $axios.post('/api/watchlists', payload),
    onSuccess: invalidate
  }, $queryClient),

  // A copy starts with the same criteria and notification settings, no changes, and its own
  // history (the server publishes its first criteria version on create).
  duplicateWatchlist: () => useMutation({
    mutationFn: watchlist => $axios.post('/api/watchlists', {
      title: `${watchlist.title} (copy)`.slice(0, 255),
      description: watchlist.description ?? null,
      filters: watchlist.filters ?? null,
      notify_in_app: watchlist.notifyInApp,
      notify_email: watchlist.notifyEmail,
      brand_ids: (watchlist.pinnedBrands ?? []).map(brand => brand.brandId),
      presentation_ids: (watchlist.pinnedPresentations ?? []).map(presentation => presentation.presentationId),
      fav_group_ids: (watchlist.favGroups ?? []).map(group => group.id)
    }),
    onSuccess: invalidate
  }, $queryClient),

  updateWatchlist: () => useMutation({
    mutationFn: ({ id, ...payload }) => $axios.put(`/api/watchlists/${id}`, payload),
    onSuccess: ({ data }, { id }) => {
      cacheWatchlist(id, data)
      invalidate()
    }
  }, $queryClient),

  // Adds without replacing what's pinned (idempotent), so callers never need a GET-merge-PUT
  // round trip that would lose a concurrent edit. The server also pins each added
  // presentation's parent brand.
  addItemsToWatchlist: () => useMutation({
    mutationFn: ({ id, brandIds = [], presentationIds = [] }) => $axios.post(`/api/watchlists/${id}/items`, {
      brand_ids: brandIds,
      presentation_ids: presentationIds
    }),
    onSuccess: ({ data }, { id }) => {
      cacheWatchlist(id, data)
      invalidate()
    }
  }, $queryClient),

  indexVersions: ({ watchlistId, enabled = true } = {}) =>
    useQuery({
      queryKey: computed(() => [...QUERY_KEY, unref(watchlistId), 'versions']),
      queryFn: async () => {
        const { data } = await $axios.get(`/api/watchlists/${unref(watchlistId)}/versions`)
        return data?.data ?? []
      },
      enabled
    }, $queryClient),

  showVersion: ({ watchlistId, number, enabled = true } = {}) =>
    useQuery({
      queryKey: computed(() => [...QUERY_KEY, unref(watchlistId), 'versions', unref(number)]),
      queryFn: async () => {
        const { data } = await $axios.get(`/api/watchlists/${unref(watchlistId)}/versions/${unref(number)}`)
        return data?.data ?? null
      },
      enabled,
      staleTime: Infinity
    }, $queryClient),

  destroyWatchlist: () => useMutation({
    mutationFn: id => $axios.delete(`/api/watchlists/${id}`),
    onSuccess: invalidate
  }, $queryClient),

  reorder: () => useMutation({
    mutationFn: orderedIds => $axios.patch('/api/watchlists/order', { watchlists: orderedIds }),
    onSuccess: invalidate
  }, $queryClient),

  // One list up to the server's cap (newest window by time), ordered by `view.sort`. Older
  // matches load through `fetchNextPage` (cursor from meta.nextCursor); everything else is a
  // single request, so there are no visible pages.
  indexChanges: ({ watchlistId, view, enabled = true } = {}) =>
    useInfiniteQuery({
      queryKey: computed(() => [...CHANGES_KEY, String(unref(watchlistId)), changesQueryParams(unref(view))]),
      initialPageParam: null,
      queryFn: async ({ pageParam }) => {
        const { data } = await $axios.get(`/api/watchlists/${unref(watchlistId)}/changes`, {
          params: { ...changesQueryParams(unref(view)), ...(pageParam ? { before: pageParam } : {}) }
        })
        return { data: data?.data ?? [], meta: data?.meta ?? {} }
      },
      getNextPageParam: lastPage => lastPage.meta?.nextCursor ?? undefined,
      enabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }, $queryClient),

  indexResults: ({ watchlistId, sort, direction, perPage = 40, enabled = true } = {}) =>
    useInfiniteQuery({
      queryKey: computed(() => [...RESULTS_KEY, String(unref(watchlistId)), unref(sort), unref(direction)]),
      initialPageParam: 1,
      queryFn: async ({ pageParam }) => {
        const { data } = await $axios.get(`/api/watchlists/${unref(watchlistId)}/results`, {
          params: { page: pageParam, per_page: perPage, sort: unref(sort), direction: unref(direction) }
        })
        return { data: data?.data ?? [], meta: data?.meta ?? {} }
      },
      // Page-based, not cursor-based: the next page exists as long as fewer rows have loaded
      // than the server-reported total.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((sum, page) => sum + page.data.length, 0)
        return loaded < (lastPage.meta?.total ?? 0) ? allPages.length + 1 : undefined
      },
      enabled,
      staleTime: 1000 * 30
    }, $queryClient),

  markSeen: () => useMutation({
    mutationFn: ({ watchlistId, changeId }) => $axios.patch(`/api/watchlists/${watchlistId}/changes/${changeId}/seen`),
    onSuccess: (_, { watchlistId, changeId }) => {
      const seenAt = new Date().toISOString()
      patchChanges(watchlistId, change => change.id === changeId && !change.seenAt ? { ...change, seenAt } : change)
      invalidate()
    }
  }, $queryClient),

  markAllSeen: () => useMutation({
    mutationFn: watchlistId => $axios.patch(`/api/watchlists/${watchlistId}/changes/seen`),
    onSuccess: (_, watchlistId) => {
      const seenAt = new Date().toISOString()
      patchChanges(watchlistId, change => change.seenAt ? change : { ...change, seenAt })
      invalidate()
    }
  }, $queryClient)
})
